import { useCallback, useMemo } from 'react';

import { TAB_DEFAULT_BASE_NAME } from '@/features/Dashboard/constants/names';
import { useMoveMenuCallbacks } from '@/features/Dashboard/pages/Edit/hooks/useMoveMenuCallbacks';
import { buildDashboardFromDraft } from '@/features/Dashboard/pages/Edit/model/utils/mapping';
import type { TileBigNumber } from '@/features/Dashboard/types';
import { useDashboard } from '@/features/Dashboard/api';
import { useAppSelector } from '@/hooks/redux-hooks';

import { useDashboardEditActions } from '../../hooks/useDashboardEditActions';
import {
  selectCurrentTabId,
  selectDashboardName,
  selectDraftTabs,
  selectInitialDashboardName,
  selectOtherDashboardDrafts,
  selectTabDrafts
} from '../../model/dashboardEdit.selectors';
import { DashboardTargetMenu } from '../menus';

import type { TabInfo } from '../menus';

interface MoveTileToDashboardMenuProps {
  tile: TileBigNumber;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onRemoveFromCurrent: () => void;
}

/**
 * Menu for moving a tile to another tab/dashboard.
 * Uses Redux actions for all operations.
 */
const MoveTileToDashboardMenu = ({
  tile,
  anchorEl,
  open,
  onClose,
  onRemoveFromCurrent
}: MoveTileToDashboardMenuProps) => {
  const currentTabId = useAppSelector(selectCurrentTabId);
  const dashboardName = useAppSelector(selectDashboardName);
  const initialDashboardName = useAppSelector(selectInitialDashboardName);
  const draftTabs = useAppSelector(selectDraftTabs);
  const tabDrafts = useAppSelector(selectTabDrafts);
  const otherDashboardDrafts = useAppSelector(selectOtherDashboardDrafts);
  const actions = useDashboardEditActions();
  const { data: allDashboards } = useDashboard();

  const effectiveCurrentDashboardName = dashboardName || initialDashboardName;

  const dashboardNamesList = useMemo(() => {
    const names = new Set(Object.keys(allDashboards || {}));

    // Add all dashboards that have drafts in session
    Object.keys(otherDashboardDrafts).forEach(name => names.add(name));
    if (dashboardName) names.add(dashboardName);
    if (initialDashboardName) names.add(initialDashboardName);

    // If draft name differs from initial, remove the old name
    if (dashboardName && dashboardName !== initialDashboardName) {
      names.delete(initialDashboardName);
    }

    return Array.from(names).sort((a, b) => {
      // Current dashboard first for 'with-tabs' variant
      if (a === effectiveCurrentDashboardName) return -1;
      if (b === effectiveCurrentDashboardName) return 1;
      return a.localeCompare(b);
    });
  }, [
    allDashboards,
    otherDashboardDrafts,
    dashboardName,
    initialDashboardName,
    effectiveCurrentDashboardName
  ]);

  const { handleCreateDashboard, handleError } = useMoveMenuCallbacks({
    createDashboardDraft: actions.createUniqueDashboardDraft
  });

  const getDashboardTabs = useCallback(
    (targetDashboardName: string) => {
      const isMainDraft =
        targetDashboardName === dashboardName ||
        (initialDashboardName && targetDashboardName === initialDashboardName);

      let tabs: Record<string, any> | null;
      if (isMainDraft) {
        tabs = buildDashboardFromDraft(draftTabs, tabDrafts);
      } else if (otherDashboardDrafts[targetDashboardName]) {
        const draft = otherDashboardDrafts[targetDashboardName];
        tabs = buildDashboardFromDraft(draft.draftTabs, draft.tabDrafts);
      } else {
        tabs = allDashboards?.[targetDashboardName] || null;
      }

      if (!tabs) return null;

      const filteredTabs: Array<TabInfo> = [];

      for (const [tabName, tabValue] of Object.entries(tabs)) {
        let tabId: string | null;
        if (isMainDraft) {
          const draftTab = draftTabs.find(t => t.name === tabName);
          tabId = draftTab?.id || null;
        } else if (otherDashboardDrafts[targetDashboardName]) {
          const draft = otherDashboardDrafts[targetDashboardName];
          const draftTab = draft.draftTabs.find(t => t.name === tabName);
          tabId = draftTab?.id || null;
        } else {
          tabId = tabValue?.id || null;
        }

        const finalTabId = tabId || tabName;

        // Exclude current tab
        if (currentTabId && finalTabId === currentTabId && isMainDraft) {
          continue;
        }

        filteredTabs.push({ tabName, tabId: finalTabId });
      }

      return filteredTabs.length > 0 ? filteredTabs : null;
    },
    [
      dashboardName,
      initialDashboardName,
      draftTabs,
      tabDrafts,
      otherDashboardDrafts,
      allDashboards,
      currentTabId
    ]
  );

  /**
   * Handle moving tile to an existing tab.
   */
  const handleSelectTab = useCallback(
    async (targetDashboard: string, tab: TabInfo) => {
      const success = await actions.moveTileToOtherDashboard(targetDashboard, tab.tabId, tile);
      if (success) {
        onRemoveFromCurrent();
      }
      onClose();
    },
    [tile, actions, onRemoveFromCurrent, onClose]
  );

  /**
   * Handle creating a new tab and moving tile to it.
   */
  const handleCreateNewTab = useCallback(
    async (targetDashboard: string) => {
      const tabId = crypto.randomUUID();
      const tabName = TAB_DEFAULT_BASE_NAME;
      actions.ensureTabExistsInOtherDashboard(targetDashboard, tabId, tabName);
      const success = await actions.moveTileToOtherDashboard(targetDashboard, tabId, tile);
      if (success) {
        onRemoveFromCurrent();
      }
      onClose();
    },
    [tile, actions, onRemoveFromCurrent, onClose]
  );

  return (
    <DashboardTargetMenu
      showChevron
      showNewPageOption
      anchorEl={anchorEl}
      currentDashboardName={effectiveCurrentDashboardName}
      dashboards={dashboardNamesList}
      excludeCurrentTabId={currentTabId}
      getTabs={getDashboardTabs}
      open={open}
      variant='with-tabs'
      onClose={onClose}
      onCreateDashboard={handleCreateDashboard}
      onCreateNewTab={handleCreateNewTab}
      onError={handleError}
      onSelectTab={handleSelectTab}
    />
  );
};

export default MoveTileToDashboardMenu;
